/**
 * « Oui, c'est mon ordinateur » : the program's sign-in, said in the browser.
 *
 * The program used to ask for the email and password in its own window, then
 * the site asked again (UX audit 03/10, lot 3). Now it opens this site at
 * `/?relier=<id>` and waits; this card shows the same code the program shows
 * and lets the signed-in person adopt it (app/features/agent/link.py).
 *
 * The request outlives a sign-up: creating an account sends an email whose
 * link opens another tab, so the id is kept in localStorage for as long as
 * the server keeps the request, not in this tab's memory.
 */
import { APIClient } from "../../api/client.js";
import { h, setChildren } from "../../components/dom.js";
import { store } from "../../state/store.js";
import type { MachineLink } from "../../types/index.js";

const KEY = "image-generator.machine-link";
const PARAM = "relier";

interface Kept {
	id: string;
	until: number;
}

function readKept(): Kept | null {
	try {
		const raw = localStorage.getItem(KEY);
		if (!raw) return null;
		const kept = JSON.parse(raw) as Kept;
		return typeof kept.id === "string" && kept.until > Date.now() ? kept : null;
	} catch {
		return null;
	}
}

function keep(id: string): void {
	try {
		localStorage.setItem(KEY, JSON.stringify({ id, until: Date.now() + 15 * 60 * 1000 }));
	} catch {
		// Private window: the request still works in this tab.
	}
}

function forget(): void {
	try {
		localStorage.removeItem(KEY);
	} catch {
		// Nothing kept, nothing to forget.
	}
}

/** The id from the address the program opened, taken out of the address. */
function takeFromAddress(): string | null {
	const params = new URLSearchParams(window.location.search);
	const id = params.get(PARAM);
	if (!id) return null;
	params.delete(PARAM);
	const rest = params.toString();
	window.history.replaceState({}, "", `${window.location.pathname}${rest ? `?${rest}` : ""}`);
	keep(id);
	return id;
}

function openWorkspace(id: string): void {
	document.querySelector<HTMLButtonElement>(`.rail-item[data-workspace="${id}"]`)?.click();
}

function describe(link: MachineLink): string {
	const parts = [link.machine.name, link.machine.system, link.machine.graphics].filter(Boolean);
	return parts.length ? parts.join(" · ") : "Un ordinateur";
}

export function createLinkRequestCard(): HTMLElement {
	const element = h("section", { class: "card link-request", hidden: true });
	let linkId = takeFromAddress() ?? readKept()?.id ?? null;
	let link: MachineLink | null = null;
	let gone: string | null = null;
	let answered: "approved" | "denied" | null = null;
	let busy = false;
	let error = "";

	async function load(): Promise<void> {
		if (!linkId) return;
		try {
			link = await APIClient.getMachineLink(linkId);
		} catch (cause) {
			link = null;
			gone =
				cause instanceof Error && cause.message
					? cause.message
					: "Cette demande a expiré. Relancez-la depuis le programme.";
			forget();
		}
		render();
	}

	async function answer(approve: boolean): Promise<void> {
		if (!linkId || busy) return;
		busy = true;
		error = "";
		render();
		try {
			await APIClient.answerMachineLink(linkId, approve);
			answered = approve ? "approved" : "denied";
			forget();
		} catch (cause) {
			error = cause instanceof Error ? cause.message : "La réponse n'a pas pu être envoyée.";
		} finally {
			busy = false;
			render();
		}
	}

	function close(): void {
		forget();
		linkId = null;
		render();
	}

	function render(): void {
		element.hidden = !linkId;
		if (!linkId) return;
		const user = store.get().user;
		const mustConfirm =
			Boolean(user) && !user?.verified && store.get().status?.email_verification_required !== false;
		const title = h("div", { class: "card-title" }, "Relier un ordinateur");

		if (answered === "approved") {
			setChildren(
				element,
				title,
				h(
					"p",
					null,
					"C'est fait : votre ordinateur se connecte à votre compte. Laissez la fenêtre du " +
						"programme ouverte, il prépare tout seul ce qu'il faut."
				),
				h(
					"button",
					{
						class: "btn btn-primary",
						type: "button",
						onclick: () => {
							close();
							openWorkspace("image");
						},
					},
					"Créer ma première image"
				)
			);
			return;
		}
		if (answered === "denied") {
			setChildren(
				element,
				title,
				h("p", null, "Refusé : cet ordinateur n'est pas relié à votre compte."),
				h("button", { class: "btn btn-small", type: "button", onclick: close }, "Fermer")
			);
			return;
		}
		if (gone || !link) {
			setChildren(
				element,
				title,
				h(
					"p",
					{ class: "help-text" },
					gone ?? "Lecture de la demande…",
					gone ? " Dans le programme, cliquez à nouveau « Relier avec mon navigateur »." : ""
				),
				gone
					? h("button", { class: "btn btn-small", type: "button", onclick: close }, "Fermer")
					: null
			);
			return;
		}

		const code = h("p", { class: "link-code" }, link.user_code);
		const machine = h("p", { class: "help-text" }, describe(link));
		if (!user) {
			setChildren(
				element,
				title,
				h(
					"p",
					null,
					"Votre ordinateur demande à être relié à un compte. Connectez-vous ou créez votre " +
						"compte ci-dessous : cette page vous demandera ensuite de confirmer."
				),
				machine,
				code
			);
			return;
		}
		if (mustConfirm) {
			setChildren(
				element,
				title,
				h(
					"p",
					null,
					"Confirmez d'abord votre adresse email : ouvrez le lien que nous venons de vous " +
						"envoyer. Revenez ensuite sur cette page, la demande vous attendra."
				),
				machine,
				code
			);
			return;
		}
		setChildren(
			element,
			title,
			h("p", null, `Relier cet ordinateur à votre compte (${user.email}) ?`),
			machine,
			h("p", { class: "help-text" }, "Le programme affiche ce code :"),
			code,
			h(
				"p",
				{ class: "help-text" },
				"Ne confirmez que si c'est le même code, dans le programme que vous venez d'ouvrir. " +
					"Un code que vous ne voyez nulle part ? Quelqu'un d'autre essaie de relier son " +
					"ordinateur à votre compte : refusez."
			),
			h(
				"div",
				{ class: "link-actions" },
				h(
					"button",
					{
						class: "btn btn-primary",
						type: "button",
						disabled: busy,
						onclick: () => void answer(true),
					},
					"Oui, c'est mon ordinateur"
				),
				h(
					"button",
					{ class: "btn", type: "button", disabled: busy, onclick: () => void answer(false) },
					"Non, refuser"
				)
			),
			error ? h("p", { class: "result-warning" }, error) : null
		);
	}

	// Signing in, confirming the email in another tab and coming back: each
	// changes what the card can offer.
	store.subscribe(
		(s) =>
			`${s.user?.user_id ?? ""}:${s.user?.verified ?? ""}:${s.status?.email_verification_required ?? ""}`,
		render
	);
	window.addEventListener("focus", () => {
		if (!linkId || answered) return;
		if (!link) void load();
		// The email confirmed in the tab its link opened: this tab's user is
		// still the unconfirmed one until asked again.
		const user = store.get().user;
		if (user && !user.verified && APIClient.hasAuthToken()) {
			APIClient.getCurrentUser()
				.then((fresh) => {
					if (fresh.verified) store.setState({ user: fresh });
				})
				.catch(() => undefined);
		}
	});
	render();
	if (linkId) {
		void load();
		// The program sent this person here for this: show it first.
		window.setTimeout(() => openWorkspace("account"), 0);
	}
	return element;
}
